import type { SingularKeys } from '@libs/i18n';

/**
 * Wire `error_code` values the AI attaches to a failed remediation item.
 * Source of truth: `apps/ai_workflow/document_generation/errors.py`
 * (`ERROR_CODE_MAP` + `DEFAULT_ERROR_CODE`).
 *
 * Two delivery-side codes are deliberately absent, so they fall through to the generic
 * message: `RESULT_NOT_DELIVERED` (the AI generated the document but it never reached
 * us) and `RESULT_DELIVERY_FAILED` (the AI could not hand it over). Neither is a
 * property of the document, and the generic copy — "could not be remediated, you can
 * try again" — is exactly the right advice for both. A bespoke message would have to
 * explain our own delivery plumbing to a compliance officer.
 */
const ITEM_ERROR_KEYS: Record<string, SingularKeys> = {
  ASSESSMENT_ARTIFACTS_UNAVAILABLE: 'app.remediation.itemError.artifactsUnavailable',
  SOURCE_DOWNLOAD_FAILED: 'app.remediation.itemError.sourceDownloadFailed',
  EXTRACTION_FAILED: 'app.remediation.itemError.extractionFailed',
  UNSUPPORTED_FORMAT: 'app.remediation.itemError.unsupportedFormat',
  CATALOG_DOCUMENT_NOT_FOUND: 'app.remediation.itemError.catalogDocumentNotFound',
  GENERATION_FAILED: 'app.remediation.itemError.generationFailed',
  LLM_UNAVAILABLE: 'app.remediation.itemError.llmUnavailable',
};

/**
 * Translation key for a failed item's reason.
 *
 * The AI's `failureReason` is an engineer's string — it carries raw ids and
 * internal artifact names ("no remediation_input artifact for
 * assessment='17dc…' document='8369…'"). It belongs in logs and support
 * tickets, never on screen, so the UI renders a message chosen by `errorCode`
 * and falls back to a generic one for a code it does not recognise.
 *
 * Returns null when the item has no error at all, so callers can render nothing
 * rather than an empty row.
 */
export function itemErrorKey(item: {
  errorCode?: string | null;
  failureReason?: string | null;
}): SingularKeys | null {
  if (!item.errorCode && !item.failureReason) return null;
  const code = item.errorCode ?? '';
  return ITEM_ERROR_KEYS[code] ?? 'app.remediation.itemError.unknown';
}
